//@ts-check
/**
 * Team Carousel Component
 * Displays 3 team member cards in a centered stack layout
 * Active card in the middle, previous and next cards peeking from above/below
 */

/** @type {{activeIndex: number, members: Array<any>, container: HTMLElement | null, scrollTimeout: ReturnType<typeof setTimeout> | null}} */
let teamCarouselState = {
    activeIndex: 0,
    members: [],
    container: null,
    scrollTimeout: null
};

/**
 * Initialize the team carousel with team members
 * @param {HTMLElement} container - The container element to render carousel into
 * @param {Array} members - Array of team member objects
 */
export function initTeamCarousel(container, members) {
    teamCarouselState.container = container;
    teamCarouselState.members = members;
    // Preserve active index if it's still valid, otherwise reset to 0
    if (teamCarouselState.activeIndex >= members.length) {
        teamCarouselState.activeIndex = 0;
    }
    renderTeamCarousel();
    attachCarouselEventHandlers();
}

/**
 * Get the current active member index
 * @returns {number} Active member index
 */
export function getCarouselIndex() {
    return teamCarouselState.activeIndex;
}

/**
 * Set the active member index
 * @param {number} index - Index to set as active
 */
export function setCarouselIndex(index) {
    if (index >= 0 && index < teamCarouselState.members.length) {
        teamCarouselState.activeIndex = index;
        renderTeamCarousel();
        attachCarouselEventHandlers();
    }
}

/**
 * Rotate the carousel to the next or previous member
 * @param {string} direction - 'next' or 'prev'
 */
export function rotateTeamCarousel(direction) {
    const { members, activeIndex } = teamCarouselState;
    let newIndex = activeIndex;

    if (direction === 'next') {
        newIndex = (activeIndex + 1) % members.length;
    } else if (direction === 'prev') {
        newIndex = (activeIndex - 1 + members.length) % members.length;
    }

    setCarouselIndex(newIndex);
}

/**
 * Calculate which member should be in which position (previous, active, next)
 * @returns {{previous: number, active: number, next: number}} Object with indices for previous, active, and next positions
 */
function calculatePositions() {
    const { activeIndex, members } = teamCarouselState;
    const length = members.length;

    return {
        previous: (activeIndex - 1 + length) % length,
        active: activeIndex,
        next: (activeIndex + 1) % length
    };
}

/**
 * Get Tailwind classes for a card based on its position
 * @param {string} position - 'previous', 'active', or 'next'
 * @returns {string} Tailwind classes for positioning and styling
 *
 * Horizontal spread: Cards offset by ~40px (1/6 of 256px card width for 2/3 overlap)
 * - active: top right
 * - next: middle
 * - previous: bottom left
*/
function getCardPositionClass(position) {
    const baseTransition = 'transition-all duration-300';

    switch (position) {
        case 'previous':
            // Bottom and left edges aligned with container, semi-transparent
            return `absolute scale-[0.90] bottom-0 left-0 saturate-[0.5] z-10 ${baseTransition}`;
        case 'next':
            // Center, fully visible
            return `absolute scale-[0.95] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 saturate-[0.75] z-20 ${baseTransition}`;
        case 'active':
            // Top and right edges aligned with container, semi-transparent
            return `absolute top-0 right-0 z-30 ${baseTransition}`;
        default:
            return baseTransition;
    }
}

/**
 * Render the team carousel HTML
 * Uses Tailwind classes for styling (no external CSS needed)
 */
/**
 * Update button interaction states based on card positions
 * Only allows buttons on the active (centered) card to be clickable
 */
function updateCardButtonStates() {
    const container = teamCarouselState.container;
    if (!container) return;

    setTimeout(() => {
        const cardDivs = container.querySelectorAll('[data-carousel-position]');
        cardDivs.forEach((/** @type {any} */ card) => {
            const position = card.getAttribute('data-carousel-position');
            const buttons = card.querySelectorAll('button');

            buttons.forEach((/** @type {any} */ button) => {
                if (position === 'active') {
                    // Active card: enable buttons
                    button.classList.remove('pointer-events-none');
                } else {
                    // Background cards: disable buttons
                    button.classList.add('pointer-events-none');
                }
            });
        });
    }, 0);
}

export function renderTeamCarousel() {
    const { container, members, activeIndex } = teamCarouselState;

    if (!container || members.length === 0) {
        return;
    }

    const positions = calculatePositions();
    const prevIndex = positions.previous;
    const actIndex = positions.active;
    const nextIndex = positions.next;

    // Build the carousel HTML using Tailwind classes
    const carouselHTML = `
        <div class="flex flex-col items-center gap-4">
            <!-- Cards Stack Container -->
            <!-- Width allows for card overlap with minimal extra space -->
            <!-- Supports click and scroll wheel controls -->
            <div id="carousel-cards-container" class="relative h-[32rem] w-[21rem] xl:w-[26rem] 2xl:w-[28rem] cursor-pointer">
                <!-- Previous Card -->
                <div class="${getCardPositionClass('previous')}" data-carousel-position="previous">
                    ${members[prevIndex]?.html || ''}
                </div>

                <!-- Active Card -->
                <div class="${getCardPositionClass('active')}" data-carousel-position="active">
                    ${members[actIndex]?.html || ''}
                </div>

                <!-- Next Card -->
                <div class="${getCardPositionClass('next')}" data-carousel-position="next">
                    ${members[nextIndex]?.html || ''}
                </div>
            </div>
        </div>
    `;

    container.innerHTML = carouselHTML;
    updateCardButtonStates();
}

/**
 * Attach event listeners to the carousel for click and scroll controls
 * - Click on cards: moves clicked card to the front
 * - Scroll up over cards: advances to next member
 * - Scroll down over cards: advances to previous member
 */
function attachCarouselEventHandlers() {
    const container = teamCarouselState.container;
    if (!container) return;

    // Wait for DOM to be ready, then attach handlers
    setTimeout(() => {
        const cardsContainer = container.querySelector('#carousel-cards-container');
        if (!cardsContainer) return;

        // Click handler: move clicked card to front
        cardsContainer.addEventListener('click', (/** @type {any} */ e) => {
            e.stopPropagation();

            // Ignore clicks on buttons or other interactive elements
            if (e.target.closest('button') || e.target.closest('[onclick]')) {
                return;
            }

            // Find which card was clicked
            const clickedElement = e.target.closest('[data-carousel-position]');
            if (!clickedElement) return;

            const position = clickedElement.getAttribute('data-carousel-position');

            // Don't transition if clicking the active card
            if (position === 'active') return;

            // Move clicked card (previous or next) to the front
            const { activeIndex, members } = teamCarouselState;
            const length = members.length;
            let newIndex = activeIndex;

            if (position === 'previous') {
                newIndex = (activeIndex - 1 + length) % length;
            } else if (position === 'next') {
                newIndex = (activeIndex + 1) % length;
            }

            setCarouselIndex(newIndex);
        });

        // Scroll wheel handler: up = next, down = prev
        cardsContainer.addEventListener('wheel', (/** @type {any} */ e) => {
            e.preventDefault();
            e.stopPropagation();

            // Debounce scroll events to prevent rapid consecutive rotations
            if (teamCarouselState.scrollTimeout) {
                clearTimeout(teamCarouselState.scrollTimeout);
            }

            teamCarouselState.scrollTimeout = setTimeout(() => {
                const direction = e.deltaY < 0 ? 'next' : 'prev';
                rotateTeamCarousel(direction);
                teamCarouselState.scrollTimeout = null;
            }, 100);
        }, { passive: false });
    }, 0);
}

/**
 * Export functions for global window access (needed for inline onclick handlers)
 */
window.teamCarouselNext = function() {
    rotateTeamCarousel('next');
};

window.teamCarouselPrev = function() {
    rotateTeamCarousel('prev');
};
